import {otsBaseApi} from "../../ots-api";
import {TagAssignment, TagAssignmentBulkRequest, TagSearchHit} from "../../../model/tag-assignment";
import {EntityIdentifier} from "../../../model/entity";
import {removeUndefined} from "../../../util/util";

const ALL_TAG_TYPES = [
  "TagAssignments",
  "ObjectTagAssignments",
  "ObjectObjectChangeLogs",
  "ContainerStatuses"
] as const;

type TagType = typeof ALL_TAG_TYPES[number];

const taggedBaseApi = otsBaseApi.enhanceEndpoints({
  addTagTypes: ALL_TAG_TYPES
});

export const tagAssignmentsApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    queryTags: builder.query<TagSearchHit[], { query: string, objectTypes?: string[] | undefined }>({
      query: (arg) => ({
        url: `/tags/query`,
        method: "GET",
        params: removeUndefined({
          query: arg.query,
          objectTypes: arg.objectTypes?.join(","),
        }),
      }),
    }),
    getAllTagAssignments: builder.query<TagAssignment[], void>({
      query: (arg) => ({
        url: `/tags/all`,
        method: "GET",
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "TagAssignments", id} as const)),
            {type: "TagAssignments", id: "LIST"}
          ];
        } else {
          return [{type: "TagAssignments", id: "LIST"}];
        }
      }
    }),
    getAllTagAssignmentsByObject: builder.query<TagAssignment[], EntityIdentifier>({
      query: ({id, type}) => ({
        url: `/tags/${type}/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, arg) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "TagAssignments", id} as const)),
            {type: "ObjectTagAssignments", id: `${arg.type}:${arg.id}`}
          ];
        } else {
          return [{type: "ObjectTagAssignments", id: `${arg.type}:${arg.id}`}];
        }
      }
    }),
    getTagAssignment: builder.query<TagAssignment, string>({
      query: (id) => ({
        url: `/tags/${id}`,
        method: "GET"
      }),
      providesTags: (result, error, id) => [{type: "TagAssignments", id}]
    }),
    createTagAssignment: builder.mutation<TagAssignment, Partial<TagAssignment>>({
      query: (mapping) => ({
        url: "/tags",
        method: "POST",
        body: mapping
      }),
      invalidatesTags: (result) => {
        const tags: { type: TagType, id?: string }[] = []

        tags.push({type: "TagAssignments", id: "LIST"})

        if (result?.objectId && result?.objectType) {
          tags.push({type: "ObjectTagAssignments", id: `${result.objectType}:${result.objectId}`})
        }

        if (result?.objectType === "CONTAINER") {
          tags.push({type: "ContainerStatuses"})
        }

        return tags
      }
    }),
    updateTagAssignment: builder.mutation<TagAssignment, Partial<TagAssignment>>({
      query: (arg) => ({
        url: `/tags`,
        method: "PUT",
        body: arg,
      }),
      invalidatesTags: (result, error, {id}) => {
        const tags: { type: TagType, id?: string }[] = []

        tags.push({type: "TagAssignments", id})
        tags.push({type: "TagAssignments", id: "LIST"})

        if (result?.objectId && result?.objectType) {
          tags.push({type: "ObjectTagAssignments", id: `${result.objectType}:${result.objectId}`})
        }

        if (result?.objectType === "CONTAINER") {
          tags.push({type: "ContainerStatuses"})
        }

        return tags
      }
    }),
    deleteTagAssignment: builder.mutation<void, Partial<TagAssignment>>({
      query: (mapping) => ({
        url: "/tags",
        method: "DELETE",
        body: mapping
      }),
      invalidatesTags: (result, error, {id, objectId, objectType}) => {
        const tags: { type: TagType, id?: string }[] = []

        tags.push({type: "TagAssignments", id})
        tags.push({type: "TagAssignments", id: "LIST"})

        if (objectId && objectType) {
          tags.push({type: "ObjectTagAssignments", id: `${objectType}:${objectId}`})
        } else {
          tags.push({type: "ObjectTagAssignments"})
        }

        if (objectType === "CONTAINER") {
          tags.push({type: "ContainerStatuses"})
        }

        return tags
      }
    }),
    replaceTagAssignmentsForObject: builder.mutation<TagAssignment[], {
      tagDefIds: string[],
      object: EntityIdentifier
    }>({
      query: ({tagDefIds, object}) => ({
        url: `/tags/${object.type}/${object.id}`,
        method: "PUT",
        body: {
          tagDefIds: tagDefIds,
        },
      }),
      invalidatesTags: (result, error, {object}) => {
        const tags: { type: TagType, id?: string }[] = []

        if (result) {
          result.forEach(tagAssignment => {
            tags.push({type: "TagAssignments", id: tagAssignment.id})
          })
        }

        tags.push({type: "TagAssignments", id: "LIST"})
        tags.push({type: "ObjectTagAssignments", id: `${object.type}:${object.id}`})

        if (object.type === "CONTAINER") {
          tags.push({type: "ContainerStatuses"})
        }

        return tags
      }
    }),
    addTagAssignmentToObject: builder.mutation<TagAssignment, { tagDefId: string, object: EntityIdentifier }>({
      query: ({tagDefId, object}) => ({
        url: `/tags/${object.type}/${object.id}/${tagDefId}`,
        method: "POST",
      }),
      invalidatesTags: (result, error, {object}) => {
        const tags: { type: TagType, id?: string }[] = []

        if (result) {
          tags.push({type: "TagAssignments", id: result.id})
        }

        tags.push({type: "TagAssignments", id: "LIST"})
        tags.push({type: "ObjectTagAssignments", id: `${object.type}:${object.id}`})

        if (object.type === "CONTAINER") {
          tags.push({type: "ContainerStatuses"})
        }

        return tags
      }
    }),
    removeTagAssignmentFromObject: builder.mutation<void, { tagDefId: string, object: EntityIdentifier }>({
      query: ({tagDefId, object}) => ({
        url: `/tags/${object.type}/${object.id}/${tagDefId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, {object}) => {
        const tags: { type: TagType, id?: string }[] = []
        tags.push({type: "TagAssignments", id: "LIST"})
        tags.push({type: "ObjectTagAssignments", id: `${object.type}:${object.id}`})
        if (object.type === "CONTAINER") {
          tags.push({type: "ContainerStatuses"})
        }
        return tags
      },
    }),
    bulkSaveTagAssignments: builder.mutation<void, TagAssignmentBulkRequest>({
      query: (req) => ({
        url: "/tags/bulk/save",
        method: "PUT",
        body: req
      }),
      invalidatesTags: (result, error, arg) => {
        const tags: { type: TagType, id?: string }[] = []
        tags.push({type: "TagAssignments"})
        tags.push({type: "ObjectTagAssignments"})

        if (arg.objects.find(o => o.type === "CONTAINER")) {
          tags.push({type: "ContainerStatuses"})
        }

        return tags
      }
    }),
  })
});

export const {
  useQueryTagsQuery,
  useLazyQueryTagsQuery,
  useGetAllTagAssignmentsQuery,
  useGetAllTagAssignmentsByObjectQuery,
  useLazyGetAllTagAssignmentsByObjectQuery,
  useGetTagAssignmentQuery,
  useCreateTagAssignmentMutation,
  useUpdateTagAssignmentMutation,
  useDeleteTagAssignmentMutation,
  useReplaceTagAssignmentsForObjectMutation,
  useAddTagAssignmentToObjectMutation,
  useRemoveTagAssignmentFromObjectMutation,
  useBulkSaveTagAssignmentsMutation,
} = tagAssignmentsApi;
